import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { IS_EXTERNAL_REQUEST } from '@abp/ng.core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { NexRefreshAuthTokenService } from './nex-refresh-auth-token.service';

@Injectable({
  providedIn: 'root',
})
export class NexRefreshAuthTokenInterceptor implements HttpInterceptor {
  private readonly refresher = inject(NexRefreshAuthTokenService);

  // Endpoints you should NOT try to refresh on (avoid loops)
  private readonly excludedPaths = ['/connect/token', '/connect/revocation', '/connect/revocat', '/connect/userinfo'];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err) => {
        // Only handle HTTP 401 here; delegate everything else
        if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
          return throwError(() => err);
        }

        // if external request, ignore
        if (req.context?.has(IS_EXTERNAL_REQUEST) && req.context?.get(IS_EXTERNAL_REQUEST) === true) {
          return throwError(() => err);
        }

        // Skip refresh for excluded endpoints (avoid loops)
        if (this.isExcluded(req.url)) {
          return throwError(() => err);
        }

        // If no refresh token, bail out to login
        if (!this.refresher.hasRefreshToken()) {
          return throwError(() => err);
        }

        // Try a single-flight refresh, then retry once
        return this.refresher.ensureValidAccessToken$().pipe(
          switchMap((ok) => {
            if (!ok) {
              return throwError(() => err);
            }

            // rerun request with new token
            return next.handle(this.addAccessToken(req, this.refresher.getAccessToken()));
          }),
          catchError(() =>
            // If retry also failed, bubble up the ORIGINAL 401 error
            // unless the error 'e' is not related to the retry (though unlikely here)
            // or if it's already the 401.
            throwError(() => err),
          ),
        );
      }),
    );
  }

  /**
   * Add the access token to the request headers
   * @param req
   * @param token
   * @private
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private addAccessToken(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
    if (token) {
      return req.clone({
        setHeaders: {
          authorization: 'Bearer ' + token,
        },
      });
    }

    return req;
  }

  /**
   * Check if the URL is excluded from refresh attempts
   * @param url
   * @private
   */
  private isExcluded(url: string): boolean {
    return this.excludedPaths.some((p) => url.includes(p));
  }
}
